'use client';

import { useEffect, useRef, useState } from 'react';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { fetcher } from '@utils/data-fetching';
import {
  buildLandingPath,
  extractUtmParams,
  hasAnalyticsConsent,
  UtmLastTouch,
} from '@utils/analytics/utm';

const FALLBACK_LOCALE: Locale = 'ro';

const getCurrentLocationData = () => {
  if (typeof window === 'undefined') {
    return {
      pathname: '/',
      search: '',
    };
  }

  return {
    pathname: window.location.pathname || '/',
    search: window.location.search.replace(/^\?/, ''),
  };
};

const getCurrentLocale = (): Locale => {
  if (typeof document === 'undefined') return FALLBACK_LOCALE;

  const htmlLocale = document.documentElement.lang;
  return (htmlLocale || FALLBACK_LOCALE) as Locale;
};

export const useUtmAttribution = () => {
  const { isAuth } = useAuthClient();
  const [consentGranted, setConsentGranted] = useState(hasAnalyticsConsent());
  const [locationData, setLocationData] = useState(getCurrentLocationData);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    const onConsentUpdate = () => setConsentGranted(hasAnalyticsConsent());
    window.addEventListener('analytics-consent-updated', onConsentUpdate as EventListener);
    return () => window.removeEventListener('analytics-consent-updated', onConsentUpdate as EventListener);
  }, []);

  useEffect(() => {
    const updateLocationData = () => setLocationData(getCurrentLocationData());
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function patchedPushState(...args) {
      originalPushState.apply(this, args);
      updateLocationData();
    };

    window.history.replaceState = function patchedReplaceState(...args) {
      originalReplaceState.apply(this, args);
      updateLocationData();
    };

    window.addEventListener('popstate', updateLocationData);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', updateLocationData);
    };
  }, []);

  const sendIfNeeded = async (payload: UtmLastTouch) => {
    const payloadKey = JSON.stringify(payload);
    if (lastSentRef.current === payloadKey) return;
    lastSentRef.current = payloadKey;
    try {
      await fetcher('/v1/user/utm-tracking', getCurrentLocale(), {
        method: 'POST',
        payload,
        stringifyPayload: true,
      });
    } catch {
      // Best-effort only.
    }
  };

  useEffect(() => {
    if (!consentGranted) return;

    const effectiveSearch = locationData.search;
    const utmParams = extractUtmParams(new URLSearchParams(effectiveSearch));
    if (!utmParams) return;

    const lastTouch: UtmLastTouch = {
      ...utmParams,
      timestamp: new Date().toISOString(),
      landing_path: buildLandingPath(locationData.pathname, effectiveSearch),
    };

    if (isAuth) {
      void sendIfNeeded(lastTouch);
    }
  }, [locationData, isAuth, consentGranted]);
};
